import { useRouteParams } from '@vueuse/router'
import { acceptHMRUpdate, defineStore } from 'pinia'
import {
  SubmissionGroupStatus,
  type SubmissionGroup,
  type SubmissionGroupWithStats,
} from '~/types/SubmissionGroup'
import { TestSubmissionGroupsService } from '~/services/api/test/test-submission-groups'
import { getTestSubmissionGroupDefault } from '~/static/data/testSubmissions/testSubmissionGroupDefault'
import type { ColorOption } from '~/types/Colors'
import { useTestSubmissionOverviewStore } from '@/stores/testSubmissionOverview'
import { useUserStore } from '@/stores/user'
import { TestService } from '~/services/api/test/test'
export const useTestSubmissionGroupsStore = defineStore(
  'testSubmissionGroups',
  () => {
    // SERVICES ==========================================
    const testSubmissionGroupsService = new TestSubmissionGroupsService()
    const { addToast } = useToastNotifications()
    const { showDialog } = useDialogService()
    const testService = new TestService()

    // META ==============================================
    const testId = useRouteParams<string>('testId')

    // GROUPS ==============================================
    const groups = ref<SubmissionGroupWithStats[]>([])

    const testSubmissionOverviewStore = useTestSubmissionOverviewStore()
    const userStore = useUserStore()

    async function populateGroups(testId: string) {
      const { data, error } =
        await testSubmissionGroupsService.getTestSubmissionGroups(testId)

      if (error !== null) {
        captureError(error)
        addToast({
          type: 'error',
          heading: 'Error fetching groups',
          message: 'There was an error fetching the groups',
          life: 5000,
        })
        return
      }

      groups.value = data
    }

    async function handleNewGroup() {
      const newGroupName = await showDialog<string | undefined>({
        type: 'input-text',
        props: {
          heading: 'New Group',
          inputPlaceholder: 'Enter group name',
          message: 'Enter a name for the new group',
          confirmActionLabel: 'Create',
        },
      })

      if (!newGroupName) {
        return
      }

      await createNewGroup(newGroupName)
    }

    async function createNewGroup(name: string) {
      if (!testId.value || typeof testId.value !== 'string') {
        captureError(
          new Error('Could not create group because there is no testId'),
        )
        return
      }

      const { error } =
        await testSubmissionGroupsService.createTestSubmissionGroup(
          testId.value,
          {
            ...getTestSubmissionGroupDefault(),
            name,
          },
        )

      if (error !== null) {
        addToast({
          type: 'error',
          heading: 'Error creating group',
          message: 'There was an error creating the group. Please try again.',
          life: 5000,
        })
        return
      }

      await $refresh()
    }

    async function deleteGroup(groupId: string) {
      if (!testId.value || typeof testId.value !== 'string') {
        captureError(
          new Error('Could not delete group because there is no testId'),
        )
        return
      }

      const { error } =
        await testSubmissionGroupsService.deleteTestSubmissionGroup(
          testId.value,
          groupId,
        )

      if (error !== null) {
        addToast({
          type: 'error',
          heading: 'Error deleting group',
          message: 'There was an error deleting the group. Please try again.',
          life: 5000,
        })
        return
      }

      await $refresh()
    }

    async function updateGroup(
      groupId: string,
      group: Partial<SubmissionGroup>,
    ) {
      if (!testId.value || typeof testId.value !== 'string') {
        captureError(
          new Error('Could not update group because there is no testId'),
        )
        return
      }

      const { error } =
        await testSubmissionGroupsService.updateTestSubmissionGroup(
          testId.value,
          groupId,
          group,
        )

      if (error !== null) {
        addToast({
          type: 'error',
          heading: 'Error updating group',
          message: 'There was an error updating the group. Please try again.',
          life: 5000,
        })
        return
      }

      await $refresh()
    }

    async function updateGroupStatus(
      groupId: string,
      status: SubmissionGroupStatus,
    ) {
      if (!testId.value || typeof testId.value !== 'string') {
        captureError(
          new Error('Could not update group status because there is no testId'),
        )
        return
      }

      const { error } =
        await testSubmissionGroupsService.updateTestSubmissionGroup(
          testId.value,
          groupId,
          {
            status,
          },
        )

      if (error !== null) {
        addToast({
          type: 'error',
          heading: 'Error updating group status',
          message:
            'There was an error updating the group status. Please try again.',
          life: 5000,
        })
        return
      }

      await $refresh()
    }

    async function updateGroupColor(groupId: string, color: ColorOption) {
      // OPTIMISTIC UPDATE
      const groupIndex = groups.value.findIndex((group) => group.id === groupId)

      if (groupIndex === -1) {
        return
      }

      const oldColor = groups.value[groupIndex].color

      groups.value[groupIndex].color = color

      if (!testId.value || typeof testId.value !== 'string') {
        captureError(
          new Error('Could not update group color because there is no testId'),
        )
        return
      }

      const { error } =
        await testSubmissionGroupsService.updateTestSubmissionGroup(
          testId.value,
          groupId,
          {
            color,
          },
        )

      if (error !== null) {
        addToast({
          type: 'error',
          heading: 'Error updating group color',
          message:
            'There was an error updating the group color. Please try again.',
          life: 5000,
        })

        groups.value[groupIndex].color = oldColor
      }
    }

    async function gradeAllSubmissions(groupId: string, regrade?: boolean) {
      if (!testId.value || typeof testId.value !== 'string') {
        return
      }

      const isLocked = await checkTestLockForGrading()

      if (!isLocked) return

      const { error } = await testSubmissionGroupsService.gradeAllSubmissions(
        testId.value,
        groupId,
        regrade,
      )

      if (error !== null) {
        addToast({
          type: 'error',
          heading: 'Error grading all submissions',
          message:
            'There was an error grading all submissions. Please try again.',
          life: 5000,
        })
        return
      }

      addToast({
        type: 'default',
        heading: 'All submissions graded',
        message: 'All submissions have been graded successfully.',
        life: 5000,
      })
    }

    async function exportCSVResults(groupId: string, withRemarks?: boolean) {
      if (!testId.value || typeof testId.value !== 'string') {
        return
      }

      const { error, data } =
        await testSubmissionGroupsService.exportCSVResults(
          testId.value,
          groupId,
          withRemarks,
        )

      if (error !== null) {
        addToast({
          type: 'error',
          heading: 'Error exporting CSV results',
          message:
            'There was an error exporting the CSV results. Please try again.',
          life: 5000,
        })
      }

      if (data) {
        window.open(data, '_blank')
      }
    }

    // TEST LOCKING =========================================
    async function checkTestLockForGrading() {
      await testSubmissionOverviewStore.$ensureInitialised()

      if (!testSubmissionOverviewStore.test) return false

      if (testSubmissionOverviewStore.test.lockedAt) return true

      const shouldLock = await showDialog({
        type: 'confirm',
        props: {
          heading: 'Lock & Grade',
          message:
            'The test needs to be locked before grading can begin. Do you want to lock the test and continue?',
          confirmButtonLabel: 'Lock & Grade',
          cancelButtonLabel: 'Cancel',
        },
      })

      if (!shouldLock) return false

      if (!userStore.user?.id) return false

      const { error } = await testService.lockTest(
        testId.value,
        userStore.user.id,
      )

      if (error !== null) {
        addToast({
          type: 'error',
          heading: 'Error locking test',
          message:
            'There was an error locking the test and grading was aborted. Please try again.',
          life: 5000,
        })

        return false
      }

      testSubmissionOverviewStore.$refresh()

      return true
    }

    // STORE MANAGER =======================================
    function reset() {
      groups.value = []
    }

    const {
      $isInitialised,
      $isInitialising,
      $initialise,
      $ensureInitialised,
      $reset,
      $refresh,
    } = useStoreManager(
      computed(() => testId.value),

      async () => {
        reset()

        if (!testId.value || typeof testId.value !== 'string') {
          return false
        }

        await populateGroups(testId.value)

        return true
      },

      reset,

      async () => {
        if (!testId.value || typeof testId.value !== 'string') {
          return
        }

        await populateGroups(testId.value)
      },
    )

    return {
      $isInitialised,
      $isInitialising,
      $initialise,
      $ensureInitialised,
      $reset,
      $refresh,
      groups,
      handleNewGroup,
      deleteGroup,
      updateGroup,
      updateGroupStatus,
      updateGroupColor,
      gradeAllSubmissions,
      exportCSVResults,
    }
  },
)

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(useTestSubmissionGroupsStore, import.meta.hot),
  )
}
